<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <!-- ===== Search ===== -->
      <md-card class="qc-card">
        <md-card-content>
          <div class="qc-search-row">
            <div class="qc-search-left">
              <p class="qc-search-label">Search by</p>
              <div class="search-options">
                <ul>
                  <li v-for="opt in orderSearchOptions" :key="opt.value">
                    <input type="radio" name="order-search-by" v-model="selectedSearchBy" :value="opt.value" />
                    <span>{{ opt.label }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="qc-search-input-wrapper">
              <md-field class="qc-search-field">
                <md-input type="search" id="search-input" clearable
                  placeholder="Search records here... Press enter to search" v-model="searchVal"
                  @keypress="handleSearch($event)"></md-input>
              </md-field>
            </div>

            <div class="qc-search-actions">
              <md-button class="qc-btn qc-btn-primary" @click="handleSearch('click')">Search</md-button>
              <md-button class="qc-btn qc-btn-secondary" @click="clearFilters">Clear</md-button>
            </div>
          </div>
        </md-card-content>
      </md-card>

      <!-- ===== Filters ===== -->
      <md-card class="qc-card light">
        <md-card-content>
          <div class="filters">
            <div v-if="!activeUser.customerID" class="filter customer">
              <p>Vendors</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.customers"
                :options="customers" label="name" :reduce="(t) => t.name" :closeOnSelect="true" />
            </div>

            <div v-if="isAdminUser" class="filter customer">
              <p>Sub Vendors</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.client" :options="clients"
                label="name" :reduce="(t) => t.name" :closeOnSelect="true" />
            </div>

            <div class="filter customer">
              <p>End Task</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="filterBy.status"
                :options="['Completed', 'Processing', 'Incomplete-QA In Process']" :closeOnSelect="true" />
            </div>

            <div class="filter">
              <p>Start From</p>
              <md-datepicker class="datepicker" v-model="filterBy.startDate" />
            </div>

            <div class="filter">
              <p>End To</p>
              <md-datepicker class="datepicker" v-model="filterBy.endDate" />
            </div>

            <div class="bottom-row">
              <div class="filter">
                <p>Show orders with items that</p>
                <div class="status-tags">
                  <span>Failed</span>
                  <md-checkbox v-model="filterBy.showFailedItems" />
                  <span>Passed-W/R</span>
                  <md-checkbox v-model="filterBy.showPassedWithRepair" />
                  <span>Are Certificates</span>
                  <md-checkbox v-model="filterBy.showCertificateOrders" />
                  <span>Shipped</span>
                  <md-checkbox v-model="filterBy.showShippedItems" />
                </div>
              </div>

              <div class="filter filter-btn">
                <md-button class="qc-btn qc-btn-primary" @click="handleFilter">Filter</md-button>
                <md-button class="qc-btn qc-btn-secondary" @click="clearFilters">Clear</md-button>
              </div>
            </div>
          </div>
        </md-card-content>
      </md-card>

      <!-- ===== Orders table ===== -->
      <md-card class="qc-card">
        <md-card-content>
          <div class="qc-table-header">
            <div class="btn-row stats_holder">
              <md-button :class="showExportOptions ? 'md-accent' : 'md-primary md-round md-small'"
                @click="toggleExportOptions">
                {{ showExportOptions ? "Cancel" : "Export" }}
              </md-button>

              <div v-if="showExportOptions" class="qc-export-options">
                <md-button class="md-primary" @click="exportCSV('all')">Export All Table Data</md-button>
                <md-button class="md-primary" @click="showSelectableTable = !showSelectableTable">
                  Export Selected Items
                </md-button>
              </div>

              <md-button class="md-round md-small" @click="showTableMenu = !showTableMenu">
                <md-icon translate="no">menu</md-icon>
              </md-button>

              <div v-if="showTableMenu" class="details-content">
                <div class="table-options">
                  <div class="header">
                    <h3>Customize Table</h3>
                  </div>
                  <ul>
                    <li v-for="option in tableOptions" :key="option">
                      <md-checkbox :value="option" v-model="selectedTableOptions">{{ option }}</md-checkbox>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="qc-table-wrapper">
            <table v-if="!showSelectableTable" class="qc-table">
              <thead>
                <tr>
                  <th @click="sortBy('Timestamp')">Date <span class="sort-icon">{{ sortIcon("Timestamp") }}</span></th>
                  <th @click="sortBy('OrderNum')">Order # <span class="sort-icon">{{ sortIcon("OrderNum") }}</span></th>
                  <th v-if="tableDataToDisplay['Purchase Orders']">Purchase Orders</th>
                  <th v-if="!activeUser.customerID" @click="sortBy('Customer')">
                    Vendors <span class="sort-icon">{{ sortIcon("Customer") }}</span>
                  </th>
                  <th v-if="isAdminUser" @click="sortBy('Client')">
                    Sub Vendors <span class="sort-icon">{{ sortIcon("Client") }}</span>
                  </th>
                  <th @click="sortBy('Quantity')">Qty <span class="sort-icon">{{ sortIcon("Quantity") }}</span></th>
                  <th @click="sortBy('FailedItems')">Failed <span class="sort-icon">{{ sortIcon("FailedItems") }}</span>
                  </th>
                  <th @click="sortBy('PassedWithRepairsItems')">
                    W/Repairs <span class="sort-icon">{{ sortIcon("PassedWithRepairsItems") }}</span>
                  </th>
                  <th @click="sortBy('SkippedItems')">Skipped <span class="sort-icon">{{ sortIcon("SkippedItems")
                      }}</span></th>
                  <th @click="sortBy('Status')">End Task <span class="sort-icon">{{ sortIcon("Status") }}</span></th>
                  <th v-if="tableDataToDisplay['SKUS']">SKUS</th>
                  <th v-if="tableDataToDisplay['Certificates']">Certificates</th>
                  <th v-if="tableDataToDisplay['Completed At']">Completed At</th>
                  <th class="no-sort">Controls</th>
                  <th v-if="activeUser.isDeveloper" class="no-sort">Dev Admin Controls</th>
                </tr>
              </thead>

              <tbody>
                <tr v-if="!displayedRows.length">
                  <td :colspan="ordersColspan" class="no-content">No orders</td>
                </tr>

                <tr v-for="order in displayedRows" :key="order.id || order.OrderNum" class="qc-main-row">
                  <td>{{ order.Timestamp | format_date_time }}</td>
                  <td>{{ order.OrderNum }}</td>
                  <td v-if="tableDataToDisplay['Purchase Orders']">
                    <div v-if="(order.PurchaseOrders || []).length" class="data-list">
                      <ul>
                        <li v-for="po in order.PurchaseOrders" :key="po">{{ po }}</li>
                      </ul>
                    </div>
                    <div v-else class="no-data">N/A</div>
                  </td>
                  <td v-if="!activeUser.customerID" translate="no">{{ order.Customer }}</td>
                  <td v-if="isAdminUser" translate="no">{{ order.Client }}</td>
                  <td>{{ order.Quantity }}</td>
                  <td>
                    <strong v-if="order.FailedItems" class="qc-strong">{{ order.FailedItems }}</strong>
                    <span v-else>0</span>
                  </td>
                  <td>{{ order.PassedWithRepairsItems || 0 }}</td>
                  <td>{{ order.SkippedItems || 0 }}</td>
                  <td>{{ order.Status }}</td>

                  <td v-if="tableDataToDisplay['SKUS']">
                    <div v-if="(order.ItemSKUs || []).length" class="data-list">
                      <ul>
                        <li v-for="sku in order.ItemSKUs" :key="sku">{{ sku }}</li>
                      </ul>
                    </div>
                    <div v-else class="no-data">N/A</div>
                  </td>

                  <td v-if="tableDataToDisplay['Certificates']">
                    <div v-if="(order.ItemCerts || []).length" class="data-list">
                      <ul>
                        <li v-for="cert in order.ItemCerts" :key="cert">{{ cert }}</li>
                      </ul>
                    </div>
                    <div v-else class="no-data">N/A</div>
                  </td>

                  <td v-if="tableDataToDisplay['Completed At']">
                    <span v-if="order.CompletedAt">{{ order.CompletedAt | format_date_time }}</span>
                    <span v-else>TBD</span>
                  </td>

                  <td class="qc-controls">
                    <md-button v-if="isAdminUser" class="md-just-icon md-flat" @click.native="handleViewModal(order)">
                      <md-icon translate="no" class="icon-green">visibility</md-icon>
                    </md-button>
                    <md-button class="md-just-icon md-flat" @click.native="showFailedItemReport(order)">
                      <md-icon translate="no" class="icon-gray">receipt_long</md-icon>
                    </md-button>
                  </td>

                  <td v-if="activeUser.isDeveloper" class="qc-controls">
                    <md-button class="md-just-icon md-flat" @click.native="deleteOrder(order)">
                      <md-icon translate="no" class="icon-red">delete</md-icon>
                    </md-button>
                    <md-button class="md-just-icon md-flat" @click.native="duplicateAndReAssign(order)">
                      <md-icon translate="no" class="icon-gray">save_as</md-icon>
                    </md-button>
                  </td>
                </tr>
              </tbody>
            </table>

            <SelectableTable v-else @onSelected="onSelected" :tableData="tableData" :tableInfo="exportItemsInfo">
              <template v-slot:btns>
                <md-button v-if="selectedExportData.length" class="qc-button" @click="exportCSV('selected')">
                  Export
                </md-button>
              </template>
            </SelectableTable>
          </div>
        </md-card-content>

        <md-divider></md-divider>

        <md-card-actions md-alignment="space-between">
          <div class="per-page">
            <label for="orders-per-page">Per page</label>
            <select id="orders-per-page" class="per-page-select" v-model="pagination.perPage">
              <option v-for="n in pagination.perPageOptions" :key="n" :value="n">{{ n }}</option>
            </select>
            <p class="card-category">Showing {{ total ? from + 1 : 0 }} to {{ to }} of {{ total }} entries</p>
          </div>

          <div v-if="isAdminUser" class="footer">
            <md-button class="qc-button" @click="showAuthModal = true">Authenticate</md-button>
          </div>

          <Pagination class="pagination-no-border pagination-success" v-model="pagination.currentPage"
            :per-page="pagination.perPage" :total="total" @update-table="fetchData"
            :allowClick="Boolean(tableData.length || searchedData.length)"></Pagination>
        </md-card-actions>
      </md-card>

      <!-- ===== Order items modal ===== -->
      <TableModal @qc-mult="(items) => handleMultipleItemsQA(items, false)" :showFilterForItemTable="true"
        :showItemFilter="true" :orderNumber="selectedOrder.id" :orderLineStatus.sync="selectedOrderLineStatus"
        :lineItems.sync="allLineItems" :showDialog.sync="showDialog" :lineItemsInfo.sync="lineItemsInfo"
        :itemsInfo="itemsInfo" @selectedLineItemAction="selectedLineItemAction" @selectedItemAction="selectedItemAction"
        @close-modal="closeTableModal()" @open-qc="openQC"
        @handleSearchForIndividualItem="handleSearchForIndividualItem">
      </TableModal>

      <!-- ===== Line item modal ===== -->
      <Modal :showBackBtn="true" @back="goBack('showModalContentForLineItem', 'showDialog')"
        @close="showModalContentForLineItem = false" :showDialog="showModalContentForLineItem">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <div class="row">
                <md-field class="field-fit">
                  <label>Vendors</label>
                  <md-input disabled :value="selectedOrder.Customer"></md-input>
                </md-field>
                <md-field class="field-120">
                  <label>Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label>Date/Time</label>
                  <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
                </md-field>
                <md-field class="field-70">
                  <label>QTY</label>
                  <md-input disabled :value="selectedLineItem.Quantity"></md-input>
                </md-field>
              </div>
              <div>
                <p>Description</p>
                <md-field>
                  <md-textarea @input="showChangesBtn = true" v-model="selectedLineItem.Description"></md-textarea>
                </md-field>
                <md-button v-if="showChangesBtn" class="qc-button" @click="saveDescription(selectedLineItem)">
                  Save Changes
                </md-button>
              </div>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <Table :tableData.sync="allItemsForSelectedLine" :tableInfo="itemsInfo"
                @selected-content="selectedItemAction"></Table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- ===== Single item modal ===== -->
      <Modal :showBackBtn="true" @back="goBack('showModalContentForItem', 'showDialog')"
        :showDialog="showModalContentForItem" @close="showModalContentForItem = false">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <div class="row">
                <md-field>
                  <label>Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label>SKU</label>
                  <md-input disabled :value="selectedItem.SKU || selectedItem.ClientSKU"></md-input>
                </md-field>
                <md-field>
                  <label>Item Type</label>
                  <md-input disabled :value="selectedItem.Type"></md-input>
                </md-field>
              </div>
              <div class="item-image">
                <img v-if="selectedItem.ImageURL" class="zoomable" :src="selectedItem.ImageURL" alt=""
                  @click="handleImageModal(selectedItem.ImageURL)" />
              </div>
            </md-card-content>
          </md-card>
        </template>
        <template v-slot:dialog-actions>
          <md-button class="btn all-text-btn" @click="openIndividualPhotoUpload">Add New Photo</md-button>
          <md-button class="qc-button" @click="openQC(selectedItem)">Q/A Item</md-button>
        </template>
      </Modal>

      <!-- ===== Image viewer ===== -->
      <Modal @close="closeImageViewer" :showSaveBtn="false" :showDialog="showImageModal" :showBackBtn="true"
        @back="goBack('showImageModal', 'showOrderReportModal')">
        <template v-slot:modalContent>
          <div class="image-container">
            <img v-if="imageView" :style="{ width: imageSize }" :src="imageView" alt="" />
            <div v-else class="no-image">Image not found</div>
          </div>
        </template>
      </Modal>

      <!-- ===== Single item Q/A ===== -->
      <QARejectionModal :isVisible="showQCModal" :item="selectedItem" :existingRejections="qcResponses.rejections"
        @close="clearQCResponses" @submit="handleRejectionSelection" />

      <!-- ===== Multiple item Q/A ===== -->
      <Modal @close="showQCModalForMult = false" :showSaveBtn="false" :showDialog="showQCModalForMult"
        :showBackBtn="false" :modalContainerStyle="{ width: '90vw !important' }">
        <template v-slot:modalContent>
          <div class="toggle_tabs">
            <div v-for="(item, t) in allFailedItems" :key="`qa-tab-${itemId(item) || t}`" class="tab"
              :class="{ active: popupTab === t }">
              <p>{{ item.CertNum || item.ItemNum }}</p>
            </div>
          </div>
          <div v-for="(item, index) in allFailedItems" :key="`qa-${itemId(item) || index}`" class="inputs">
            <transition name="fade">
              <div v-if="popupTab === index">
                <QAMultipleItems :item="item" :lastItem="popupTab === allFailedItems.length - 1"
                  @submitForAll="handleQAForAllSelectedItems"
                  @submit="handleQAPerItem($event, item, popupTab === allFailedItems.length - 1)"
                  @submitMultQAData="finishQCProtocol" />
              </div>
            </transition>
          </div>
        </template>
      </Modal>

      <!-- ===== Authenticate ===== -->
      <Modal @save="handleAuthChanges" @close="closeAuthModal" :showSaveBtn="Boolean(selectedAuthItems.length)"
        :showBackBtn="false" :showDialog="showAuthModal">
        <template v-slot:modalContent>
          <md-card class="auth-card">
            <md-card-content>
              <div class="filters">
                <md-field class="column">
                  <label>Search By Order/Line #</label>
                  <md-input @keydown.enter.prevent="handleAuthSearchOnce" v-model="searchAuthItems"></md-input>
                </md-field>
                <md-button class="qc-button" @click="handleAuthSearchOnce">Find</md-button>
              </div>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <table class="table-wide">
                <tr class="qc-title">
                  <th></th>
                  <th>Order #</th>
                  <th>Line #</th>
                  <th>Cert/Item #</th>
                  <th>SKU</th>
                  <th>Type</th>
                  <th v-for="opt in authStatusOptions" :key="`all-${opt.value}`">
                    <label class="auth-all">
                      <input type="checkbox" :checked="statusForAllItems === opt.value"
                        @change="handleStatuses($event, opt.value)" />
                      {{ opt.label }}
                    </label>
                  </th>
                </tr>
                <tr v-for="(item, index) in allItemsForAuthentication" :key="item.id">
                  <td>{{ index + 1 }}</td>
                  <td class="table-center">{{ item.OrderNum }}</td>
                  <td class="table-center">{{ item.LineNum }}</td>
                  <td class="table-center">{{ item.CertNum || item.ItemNum }}</td>
                  <td class="table-center">{{ item.SKU || item.ClientSKU }}</td>
                  <td class="table-center">{{ item.Type }}</td>
                  <td v-for="opt in authStatusOptions" :key="`${item.id}-${opt.value}`" class="table-center">
                    <input type="radio" :name="`auth-${item.id}`" :value="opt.value" v-model="item.Status"
                      @change="handleSelectedAuthItem(item, 'add')" />
                  </td>
                </tr>
              </table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- ===== QA associate picker ===== -->
      <Modal @save="saveAuthChanges" :showSaveBtn="true" :showBackBtn="false" :showDialog="showQAAssociateModal">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <p>Select QA Associate</p>
              <v-select :appendToBody="true" :deselectFromDropdown="true" v-model="selectedQaAssociate"
                :options="allQaAssociates" :clearable="false" :closeOnSelect="true" />
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- ===== Order report ===== -->
      <Modal @close="closeReportModal" :showDialog="showOrderReportModal" :showBackBtn="false"
        :modalContainerStyle="{ overflow: 'hidden' }">
        <template v-slot:modalContent>
          <md-card class="qc-card">
            <md-card-content>
              <div class="qc-search-row">
                <div class="qc-search-left">
                  <p class="qc-search-label">Search by</p>
                  <div class="search-options">
                    <ul>
                      <li v-for="opt in reportSearchOptions" :key="opt.value">
                        <input type="radio" name="report-search-by" v-model="selectedSearchByForAllOrderItems"
                          :value="opt.value" />
                        <span>{{ opt.label }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="qc-search-input-wrapper">
                  <md-field class="qc-search-field">
                    <md-input v-model="reportSearchValue" placeholder="Search Item Here..."></md-input>
                  </md-field>
                </div>
              </div>
            </md-card-content>
          </md-card>

          <md-divider></md-divider>

          <md-card class="qc-card">
            <md-card-content>
              <div class="qc-table-header">
                <md-field>
                  <label class="qc-header-label">Customer</label>
                  <md-input disabled :value="selectedOrder.Customer"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">Order #</label>
                  <md-input disabled :value="selectedOrder.OrderNum"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">Date/Time</label>
                  <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
                </md-field>
                <md-field>
                  <label class="qc-header-label">QTY</label>
                  <md-input disabled :value="selectedOrder.Quantity"></md-input>
                </md-field>
              </div>
            </md-card-content>

            <md-divider></md-divider>

            <div class="toggle_tabs" role="tablist">
              <button v-for="tab in reportTabs" :key="tab" type="button" role="tab" class="tab"
                :class="{ active: tableTab === tab }" :aria-selected="tableTab === tab" @click="tableTab = tab">
                {{ tab }}
              </button>
            </div>

            <transition name="fade" mode="out-in">
              <!-- Line Item Status -->
              <md-card-content v-if="tableTab === 'Line Item Status'" key="line-items">
                <div class="qc-table-wrapper">
                  <table class="qc-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>P.O #</th>
                        <th>SKU</th>
                        <th>Type</th>
                        <th>QTY</th>
                        <th>Failed</th>
                        <th>W/ Repair</th>
                        <th>Skipped</th>
                        <th>Notes</th>
                        <th>QA Images</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!reportLineRows.length">
                        <td colspan="10" class="no-content">No line items</td>
                      </tr>
                      <tr v-for="line in reportLineRows" :key="`line-${line.LineNum}`" class="qc-main-row"
                        :class="{ 'failed-items': line.failedQty || line.repairedQty }">
                        <td>{{ line.LineNum }}</td>
                        <td>{{ line.PO || "N/A" }}</td>
                        <td>
                          <div class="sku-container">{{ line.ClientSKU || "N/A" }}</div>
                        </td>
                        <td>{{ line.ItemType }}</td>
                        <td>{{ line.Quantity }}</td>
                        <td>{{ line.failedQty }}</td>
                        <td>{{ line.repairedQty }}</td>
                        <td>{{ line.skippedQty }}</td>
                        <td>
                          <div class="qc-cell-stack">
                            <ol v-if="line.Notes.length" class="notes">
                              <li v-for="(note, i) in line.Notes" :key="`note-${i}`" class="result-list-item">
                                {{ i + 1 }}. {{ note }}
                              </li>
                            </ol>
                            <div v-else class="no-content">N/A</div>
                          </div>
                        </td>
                        <td>
                          <div class="qc-cell-stack">
                            <button v-if="line.Images.length" type="button" class="btn all-text-btn"
                              @click="openImages(line, 'line')">
                              View
                            </button>
                            <div v-else class="no-content">N/A</div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </md-card-content>

              <!-- Item Status, grouped by line -->
              <md-card-content v-else key="items">
                <div class="qc-table-wrapper">
                  <table class="qc-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>P.O #</th>
                        <th>SKU</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th>QA Images</th>
                        <th>Shipped</th>
                        <th v-if="showQAAssociate">Authenticated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!groupedOrderItems.length">
                        <td :colspan="itemColspan" class="no-content">No items</td>
                      </tr>

                      <template v-for="group in groupedOrderItems">
                        <tr :key="`group-${group.lineNum}`" class="qc-line-header">
                          <td :colspan="itemColspan">
                            Line {{ group.lineNum }}
                            <span class="qc-line-count">
                              {{ group.items.length }} item{{ group.items.length === 1 ? "" : "s" }}
                              <template v-if="group.passedCount">
                                · <span class="qc-line-passed">{{ group.passedCount }} passed</span>
                              </template>
                              <template v-if="group.failedCount">
                                · <span class="qc-line-failed">{{ group.failedCount }} failed</span>
                              </template>
                              <template v-if="group.repairCount">
                                · <span class="qc-line-repair">{{ group.repairCount }} passed-w/repair</span>
                              </template>
                              <template v-if="group.skippedCount">
                                · <span class="qc-line-skipped">{{ group.skippedCount }} skipped</span>
                              </template>
                            </span>
                          </td>
                        </tr>

                        <tr v-for="item in group.items" :key="`item-${item.id}`" class="qc-main-row"
                          :class="{ 'failed-items': item.Status === 'Failed' }">
                          <td>{{ item.LineNum }}</td>
                          <td>{{ item.PO || "N/A" }}</td>
                          <td>
                            <div class="sku-container">{{ item.SKU || "N/A" }}</div>
                          </td>
                          <td>{{ item.Type }}</td>
                          <td>{{ item.Status }}</td>
                          <td>
                            <div class="qc-cell-stack">
                              <div v-if="noteLines(item).length"
                                :class="{ expanded_notes: noteLines(item).length > 1 }">
                                <div v-for="(note, i) in noteLines(item)" :key="`note-${i}`" class="qc-note-line">
                                  {{ note }}
                                </div>
                              </div>
                              <button type="button" class="btn all-text-btn" @click="handleNoteModal(item)">
                                {{ noteLines(item).length ? "Edit" : "Add Notes" }}
                              </button>
                            </div>
                          </td>
                          <td>
                            <div class="qc-cell-stack">
                              <button v-if="item.Images.length" type="button" class="btn all-text-btn"
                                @click="openImages(item)">
                                View
                              </button>
                              <button type="button" class="btn all-text-btn" @click="openImages(item)">Upload</button>
                            </div>
                          </td>
                          <td v-if="item.HasBeenShipped">
                            <div class="qc-shipped-date">{{ item.ShippedOn | format_date_time }}</div>
                            <sub v-if="item.TrackingNum">
                              <a style="color: #253c6e !important;
  text-decoration: underline;" :href="trackingUrl(item.TrackingNum)" target="_blank" rel="noopener noreferrer">
                                {{ item.TrackingNum }}
                              </a>
                            </sub>
                          </td>
                          <td v-else>Not Shipped</td>
                          <td v-if="showQAAssociate">{{ item.QA_Associate || "TBD" }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </md-card-content>
            </transition>
          </md-card>
        </template>
      </Modal>

      <!-- ===== Notes ===== -->
      <Modal :showDialog="showNoteModal" :showBackBtn="false" :showSaveBtn="true" @close="closeNoteModal"
        @save="handleSaveNote">
        <template v-slot:modalContent>
          <div class="notes-container">
            <h3>Notes:</h3>
            <md-field>
              <md-textarea v-model="noteDraft"></md-textarea>
            </md-field>
          </div>
        </template>
      </Modal>

      <!-- ===== Item images ===== -->
      <Modal @close="closeImageModal" :showDialog="showItemImagesModal" :showBackBtn="true"
        @back="goBack('showItemImagesModal', 'showOrderReportModal')">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content>
              <MultImages :media="selectedFailedItem.Images || []" :item="selectedFailedItem" @removeImage="removeImage"
                @uploadedImages="uploadedImages" />
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <!-- ===== Who authenticated ===== -->
      <Modal @close="closeAuthViewModal" :showDialog="showAuthViewModal" :showBackBtn="false">
        <template v-slot:modalContent>
          <md-card>
            <md-card-content class="row">
              <md-field>
                <label>Vendors</label>
                <md-input disabled :value="selectedOrder.Customer"></md-input>
              </md-field>
              <md-field>
                <label>Order #</label>
                <md-input disabled :value="selectedOrder.OrderNum"></md-input>
              </md-field>
              <md-field>
                <label>Date/Time</label>
                <md-input disabled :value="selectedOrder.Timestamp | format_date_time"></md-input>
              </md-field>
              <md-field>
                <label>QTY</label>
                <md-input disabled :value="selectedOrder.Quantity"></md-input>
              </md-field>
            </md-card-content>
            <md-divider></md-divider>
            <md-card-content>
              <table class="table-wide">
                <tr class="qc-title">
                  <th>Line #</th>
                  <th>Cert/Item #</th>
                  <th>SKU</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th v-if="isAdminUser">Authenticated By</th>
                </tr>
                <tr v-for="item in allLineItems" :key="item.id" class="text-center">
                  <td>{{ item.LineNum }}</td>
                  <td>{{ item.CertNum || item.ItemNum }}</td>
                  <td>{{ item.SKU || item.ClientSKU }}</td>
                  <td>{{ item.Type }}</td>
                  <td>{{ item.Status }}</td>
                  <td v-if="isAdminUser">
                    {{ item.QA_Associate || selectedOrder.QA_Associate || "Not Authenticated" }}
                  </td>
                </tr>
              </table>
            </md-card-content>
          </md-card>
        </template>
      </Modal>

      <DuplicateOrderModal @modalClose="showDuplicateModal = false" :modelValue="showDuplicateModal"
        :order="selectedOrderForDuplication" :clients="clients" />

      <input id="barcode-input" ref="barcodeInput" class="barcode-input" type="search" autofocus
        placeholder="Search or Scan records here" />
    </div>

    <md-snackbar md-position="center" :md-duration="5000" :md-active.sync="showSnackbar" md-persistent>
      <span>{{ snackbarMsg }}</span>
    </md-snackbar>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import moment from "moment";
import firebase from "firebase";
import Pagination from "@/components/Pagination.vue";
import TableModal from "@/components/TableModal.vue";
import Modal from "@/components/Modal.vue";
import Table from "@/components/Table.vue";
import SelectableTable from "@/components/SelectableTable.vue";
import MultImages from "@/components/Inputs/UploadMultiple.vue";
import DuplicateOrderModal from "@/components/DuplicateOrderModal.vue";
import { db, functions } from "@/config/firebaseInit.js";
import QARejectionModal from "../components/QARejectionModal.vue";
import QAMultipleItems from "../components/QAMultipleItems.vue";

const ExcelJS = require("exceljs");
const { DateTime } = require("luxon");

/* ---------- constants ---------- */

const CERT_SERVICE_TYPE = "Screening,QA,Certificate,DropShip";
const BATCH_LIMIT = 500;
const SCANNER_KEY_GAP_MS = 100; // scanners type faster than this; people don't
const SCANNER_MIN_LENGTH = 4;

// Filter checkbox → numeric counter on the order doc (order matches when > 0).
const COUNTER_TOGGLES = [
  ["showFailedItems", "FailedItems"],
  ["showPassedWithRepair", "PassedWithRepairsItems"],
  ["showShippedItems", "ShippedItemsQty"],
];

// Search-by option → array field on the order doc.
const ORDER_ARRAY_FIELDS = { po: "PurchaseOrders", sku: "ItemSKUs", cert: "ItemCerts" };

const defaultFilters = () => ({
  showFailedItems: false,
  showPassedWithRepair: false,
  showCertificateOrders: false,
  showShippedItems: false,
  client: "",
  status: "",
  customers: "",
  startDate: null,
  endDate: null,
});

/* ---------- helpers ---------- */

// Item doc ID. Items are stored under their StockNum/ItemNum.
const itemId = (item) => (item ? item.id || item.ItemNum || item.StockNum : undefined);

const getUserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";

// The picked calendar date, interpreted in the user's zone.
function toZonedDate(value, zone) {
  if (value instanceof Date) {
    return DateTime.fromObject(
      { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() },
      { zone },
    );
  }
  return DateTime.fromISO(value, { zone });
}

const toDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

const byTimestampDesc = (a, b) => (b.Timestamp || 0) - (a.Timestamp || 0);

export default {
  name: "Home",
  components: {
    Pagination,
    TableModal,
    Modal,
    Table,
    MultImages,
    SelectableTable,
    DuplicateOrderModal,
    QARejectionModal,
    QAMultipleItems,
  },

  data() {
    return {
      activeUser: {},

      // search & filters
      orderSearchOptions: [
        { label: "Order Number", value: "on" },
        { label: "PO #", value: "po" },
        { label: "SKU", value: "sku" },
        { label: "Certificates", value: "cert" },
      ],
      selectedSearchBy: "on",
      searchVal: "",
      filterBy: defaultFilters(),
      isSearchActive: false,
      searchedData: [],
      filterRequestId: 0,

      // table
      tableData: [],
      tableSize: 0,
      currentSort: "Timestamp",
      currentSortOrder: "desc",
      showTableMenu: false,
      tableOptions: ["Purchase Orders", "SKUS", "Certificates", "Completed At"],
      selectedTableOptions: ["Purchase Orders"],
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [50, 250, 500, 1000],
      },
      customers: [],
      clients: [],

      // export
      showExportOptions: false,
      showSelectableTable: false,
      selectedExportData: [],
      exportItemsInfo: [
        { label: "Order #", key: "OrderNum" },
        { label: "Vendor", key: "Customer" },
        { label: "Sub Vendor", key: "Client" },
        { label: "Quantity", key: "Quantity" },
        { label: "FailedItems", key: "FailedItems" },
        { label: "Status", key: "Status" },
      ],

      // order / item modals
      selectedOrder: {},
      selectedOrderLineStatus: [],
      allLineItems: [],
      allLineItemsUnfiltered: [],
      individualSearch: null,
      allItemsForSelectedLine: [],
      selectedLineItem: {},
      selectedItem: {},
      showDialog: false,
      showModalContentForLineItem: false,
      showModalContentForItem: false,
      showChangesBtn: false,
      lineItemsInfo: [
        { label: "Line #", key: "LineNum" },
        { label: "Qty", key: "Quantity" },
      ],
      itemsInfo: [
        { label: "Line #", key: "LineNum" },
        { label: "Item #", key: "ItemNum" },
        { label: "Vendor", key: "Customer" },
        { label: "SKU", key: "SKU" },
        { label: "Cert #", key: "CertNum" },
        { label: "Type", key: "Type" },
        { label: "Status", key: "Status" },
      ],

      // images
      showImageModal: false,
      showItemImagesModal: false,
      imageView: null,
      imageSize: "100%",
      selectedFailedItem: {},
      selectedItemType: null,

      // single Q/A
      showQCModal: false,
      leftQCPanelInit: false,
      qcResponses: { rejections: [], notes: "" },

      // multi Q/A
      showQCModalForMult: false,
      allFailedItems: [],
      popupTab: null,
      isBeingHandledThroughAuthWindow: false,

      // authentication
      showAuthModal: false,
      showQAAssociateModal: false,
      showAuthViewModal: false,
      isSearchingAuth: false,
      searchAuthItems: "",
      orderBeingAuthenticated: null,
      allItemsForAuthentication: [],
      selectedAuthItems: [],
      statusForAllItems: null,
      selectedQaAssociate: "",
      allQaAssociates: [],
      authStatusOptions: [
        { label: "Pass", value: "Passed" },
        { label: "Failed", value: "Failed" },
        { label: "Skip", value: "Skipped" },
        { label: "Passed-W/Repair", value: "Passed-W/Repair" },
      ],

      // report
      showOrderReportModal: false,
      reportTabs: ["Line Item Status", "Item Status"],
      tableTab: "Line Item Status",
      allOrderItemsInfo: {},
      allOrderLineInfo: {},
      reportSearchOptions: [
        { label: "Line Number", value: "LineNum" },
        { label: "P.O #", value: "PO" },
        { label: "SKU", value: "SKU" },
        { label: "Certificates", value: "CertNum" },
        { label: "Item", value: "ItemNum" },
      ],
      selectedSearchByForAllOrderItems: "LineNum",
      reportSearchValue: "",

      // notes
      showNoteModal: false,
      noteItem: null,
      noteDraft: "",

      // misc
      showDuplicateModal: false,
      selectedOrderForDuplication: {},
      showSnackbar: false,
      snackbarMsg: "",
      barcodeValue: "",
      certificateAPI: functions.httpsCallable("certificateAPI-updateCertificate"),
    };
  },

  computed: {
    isAdminUser() {
      return !!(this.activeUser.isAdmin || this.activeUser.isSuperAdmin);
    },
    showQAAssociate() {
      return !!(this.activeUser.isAdmin && this.activeUser.isSuperAdmin);
    },
    tableDataToDisplay() {
      return this.tableOptions.reduce((acc, opt) => {
        acc[opt] = this.selectedTableOptions.includes(opt);
        return acc;
      }, {});
    },
    ordersColspan() {
      let cols = 8; // date, order, qty, failed, repairs, skipped, end task, controls
      if (this.tableDataToDisplay["Purchase Orders"]) cols++;
      if (!this.activeUser.customerID) cols++;
      if (this.isAdminUser) cols++;
      if (this.tableDataToDisplay.SKUS) cols++;
      if (this.tableDataToDisplay.Certificates) cols++;
      if (this.tableDataToDisplay["Completed At"]) cols++;
      if (this.activeUser.isDeveloper) cols++;
      return cols;
    },

    /* pagination */
    total() {
      return this.isSearchActive ? this.searchedData.length : this.tableSize;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to() {
      return Math.min(this.from + this.pagination.perPage, this.total);
    },
    queriedData() {
      return this.isSearchActive ? this.searchedData.slice(this.from, this.to) : this.tableData;
    },
    // Sorting applies to the rows on the current page.
    displayedRows() {
      const field = this.currentSort;
      const dir = this.currentSortOrder === "asc" ? 1 : -1;
      return [...this.queriedData].sort((a, b) => {
        const av = a[field] ?? "";
        const bv = b[field] ?? "";
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
    },

    /* report */
    itemColspan() {
      return this.showQAAssociate ? 9 : 8;
    },
    isReportSearching() {
      return Boolean(this.reportSearchValue.trim());
    },
    reportFilteredItems() {
      const items = Object.values(this.allOrderItemsInfo);
      if (!this.isReportSearching) return items;
      const query = this.reportSearchValue.trim().toLowerCase();
      const key = this.selectedSearchByForAllOrderItems;
      return items.filter((item) =>
        key === "LineNum"
          ? String(item.LineNum) === query
          : String(item[key] ?? "").toLowerCase().includes(query),
      );
    },
    reportLineRows() {
      let lines = Object.values(this.allOrderLineInfo);
      if (this.isReportSearching) {
        const matchedLines = new Set(this.reportFilteredItems.map((i) => String(i.LineNum)));
        lines = lines.filter((l) => matchedLines.has(String(l.LineNum)));
      }
      return lines.sort((a, b) => Number(a.LineNum) - Number(b.LineNum));
    },
    groupedOrderItems() {
      const sorted = [...this.reportFilteredItems].sort((a, b) => Number(a.LineNum) - Number(b.LineNum));
      const groups = new Map();
      sorted.forEach((item) => {
        const lineNum = item.LineNum != null ? item.LineNum : "Unassigned";
        if (!groups.has(lineNum)) groups.set(lineNum, []);
        groups.get(lineNum).push(item);
      });
      const count = (items, status) => items.filter((i) => i.Status === status).length;
      return [...groups.entries()].map(([lineNum, items]) => ({
        lineNum,
        items,
        passedCount: count(items, "Passed"),
        failedCount: count(items, "Failed"),
        repairCount: count(items, "Passed-W/Repair"),
        skippedCount: count(items, "Skipped"),
      }));
    },
  },

  watch: {
    "pagination.perPage"() {
      this.pagination.currentPage = 1;
      if (!this.isSearchActive) this.fetchData("start");
    },
    // Keep the date range valid without overwriting a valid choice.
    "filterBy.startDate"(start) {
      const end = this.filterBy.endDate;
      if (start && (!end || moment(end).isBefore(moment(start), "day"))) {
        this.filterBy.endDate = start;
      }
    },
    "filterBy.endDate"(end) {
      const start = this.filterBy.startDate;
      if (end && start && moment(end).isBefore(moment(start), "day")) {
        this.filterBy.startDate = end;
      }
    },
    // Keep the order-items modal live while respecting an active search.
    allLineItemsUnfiltered() {
      this.applyIndividualSearch();
    },
  },

  created() {
    this.activeUser = this.$store.state.user || {};
    this.ordersRef = db.collection("orders");
    this._unsubscribe = null;
    this._lastKeyAt = 0;

    if (this.activeUser.isSuperAdmin) this.getQAAssociate();
    else this.selectedQaAssociate = this.activeUser.fullname;

    this._unsubAnalytics = db.doc("analytics/orders").onSnapshot(
      (snap) => {
        this.tableSize = (snap.exists && snap.data().total) || 0;
      },
      (err) => console.error("Order count listener failed:", err),
    );

    this.fetchClients();
    this.fetchCustomers();
    this.fetchData("start");
  },

  mounted() {
    window.addEventListener("keydown", this.handleBarcodeScanned);
    this.$nextTick(() => {
      const input = this.$refs.barcodeInput;
      if (input && typeof input.focus === "function") input.focus();
    });
  },

  // Vue 2 hook (beforeUnmount is Vue 3 and never ran).
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleBarcodeScanned);
    this.unbindListener();
    if (this._unsubAnalytics) this._unsubAnalytics();
  },

  methods: {
    itemId,

    /* ===================== small helpers ===================== */

    notify(msg) {
      this.snackbarMsg = msg;
      this.showSnackbar = true;
    },
    noteLines(item) {
      const notes = item.Responses && item.Responses.notes;
      return notes ? notes.split("\n").filter((n) => n.trim()) : [];
    },
    trackingUrl(trackingNum) {
      return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(trackingNum)}`;
    },
    goBack(cur, prev) {
      this[cur] = false;
      this[prev] = true;
    },
    sortBy(field) {
      if (this.currentSort === field) {
        this.currentSortOrder = this.currentSortOrder === "asc" ? "desc" : "asc";
      } else {
        this.currentSort = field;
        this.currentSortOrder = "asc";
      }
    },
    sortIcon(field) {
      if (this.currentSort !== field) return "";
      return this.currentSortOrder === "asc" ? "▲" : "▼";
    },
    toggleExportOptions() {
      if (this.showExportOptions) {
        this.showExportOptions = false;
        this.showSelectableTable = false;
        this.selectedExportData = [];
      } else {
        this.showExportOptions = true;
      }
    },
    onSelected(rows) {
      this.selectedExportData = rows;
    },

    /* ===================== lookups ===================== */

    async fetchClients() {
      try {
        this.clients = toDocs(await db.collection("clients").get());
      } catch (err) {
        console.error("Failed to load clients:", err);
      }
    },
    async fetchCustomers() {
      try {
        this.customers = toDocs(await db.collection("customers").get());
      } catch (err) {
        console.error("Failed to load customers:", err);
      }
    },
    async getQAAssociate() {
      try {
        const snap = await db.doc("attributes/qa_associates").get();
        this.allQaAssociates = (snap.exists && snap.data().employees) || [];
      } catch (err) {
        console.error("Failed to load QA associates:", err);
      }
    },

    /* ===================== live paginated orders ===================== */

    unbindListener() {
      if (typeof this._unsubscribe === "function") {
        this._unsubscribe();
        this._unsubscribe = null;
      }
    },

    async fetchData(action = "start") {
      if (this.isSearchActive) return false;

      const loader = this.$loading.show({ container: null, canCancel: false });
      let hidden = false;
      const hide = () => {
        if (!hidden) {
          hidden = true;
          loader.hide();
        }
      };

      try {
        this.unbindListener();
        const perPage = this.pagination.perPage;
        const base = this.ordersRef.orderBy("Timestamp", "desc");

        // Cursor from the doc snapshot, or its Timestamp if the doc was deleted.
        const cursorFor = async (row) => {
          const snap = await this.ordersRef.doc(row.id).get();
          return snap.exists ? snap : row.Timestamp;
        };

        let query;
        if (action === "next") {
          const last = this.tableData[this.tableData.length - 1];
          if (!last) return hide();
          query = base.startAfter(await cursorFor(last)).limit(perPage);
        } else if (action === "prev") {
          const first = this.tableData[0];
          if (!first) return hide();
          query = base.endBefore(await cursorFor(first)).limitToLast(perPage);
        } else {
          query = base.limit(perPage);
        }

        this._unsubscribe = query.onSnapshot(
          (snap) => {
            this.tableData = toDocs(snap);
            hide();
          },
          (err) => {
            console.error("Orders listener failed:", err);
            hide();
          },
        );
      } catch (err) {
        console.error("Error fetching orders:", err);
        hide();
      }
    },

    /* ===================== search & filter ===================== */

    // Shared runner: loader, stale-response guard, empty handling.
    async runOrderQuery(fetchRows) {
      this.filterRequestId += 1;
      const requestId = this.filterRequestId;
      const loader = this.$loading.show({ container: null, canCancel: false });

      try {
        const rows = await fetchRows();
        if (requestId !== this.filterRequestId) return;

        this.unbindListener();
        this.isSearchActive = true;
        this.searchedData = rows.sort(byTimestampDesc);
        this.pagination.currentPage = 1;

        if (!rows.length) Swal.fire("", "No Records Have Been Found", "info");
      } catch (err) {
        console.error("Order query failed:", err);
        if (requestId === this.filterRequestId) {
          Swal.fire(
            "Internal Issue",
            "Oops there seems to be an issue please try again or contact DIS support",
            "warning",
          );
        }
      } finally {
        loader.hide();
      }
    },

    async handleSearch(e) {
      if (e !== "click" && (!e || e.key !== "Enter")) return;

      const value = (this.searchVal || "").replace(/\s/g, "");
      if (!value) return this.clearFilters();

      const searchBy = this.selectedSearchBy;
      await this.runOrderQuery(async () => {
        if (searchBy === "on") {
          const snap = await this.ordersRef.doc(value.split("-")[0]).get();
          return snap.exists ? [{ id: snap.id, ...snap.data() }] : [];
        }
        const field = ORDER_ARRAY_FIELDS[searchBy];
        return toDocs(await this.ordersRef.where(field, "array-contains", value).get());
      });
    },

    getDateRange() {
      const { startDate, endDate } = this.filterBy;
      const zone = getUserZone();
      return {
        start: startDate ? toZonedDate(startDate, zone).startOf("day").toMillis() : null,
        end: endDate ? toZonedDate(endDate, zone).endOf("day").toMillis() : null,
      };
    },

    /**
     * Equality filters run on the server. With no equality filters, ONE
     * range filter runs on the server too (dates first, else the first
     * counter). Everything else is checked in the browser. This needs
     * no composite indexes.
     */
    async handleFilter() {
      const f = this.filterBy;

      const equality = [];
      if (f.customers) equality.push(["Customer", f.customers]);
      if (f.client) equality.push(["Client", f.client]);
      if (f.status) equality.push(["Status", f.status]);
      if (f.showCertificateOrders) equality.push(["ServiceType", CERT_SERVICE_TYPE]);

      const counterFields = COUNTER_TOGGLES.filter(([key]) => f[key]).map(([, field]) => field);
      const { start, end } = this.getDateRange();
      const hasDates = start != null || end != null;

      if (!equality.length && !counterFields.length && !hasDates) return this.clearSearch();

      let ref = this.ordersRef;
      equality.forEach(([field, value]) => {
        ref = ref.where(field, "==", value);
      });
      if (!equality.length) {
        if (hasDates) {
          if (start != null) ref = ref.where("Timestamp", ">=", start);
          if (end != null) ref = ref.where("Timestamp", "<=", end);
        } else {
          ref = ref.where(counterFields[0], ">", 0);
        }
      }

      const matches = (order) =>
        (start == null || order.Timestamp >= start) &&
        (end == null || order.Timestamp <= end) &&
        counterFields.every((field) => (order[field] || 0) > 0);

      await this.runOrderQuery(async () => toDocs(await ref.get()).filter(matches));
    },

    clearSearch() {
      this.filterRequestId += 1; // drop any in-flight query
      this.isSearchActive = false;
      this.searchedData = [];
      this.pagination.currentPage = 1;
      this.fetchData("start");
    },

    // Kept for Pagination / other callers.
    resetTable() {
      this.clearSearch();
    },

    clearFilters() {
      this.filterBy = defaultFilters();
      this.searchVal = "";
      this.clearSearch();
    },

    /* ===================== barcode scanner ===================== */

    handleBarcodeScanned(e) {
      const t = e.target;
      const isTypingInField =
        t &&
        t.id !== "barcode-input" &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (isTypingInField) return;

      const now = Date.now();
      if (now - this._lastKeyAt > SCANNER_KEY_GAP_MS) this.barcodeValue = "";
      this._lastKeyAt = now;

      if (e.key === "Enter") {
        const code = this.barcodeValue.trim();
        this.barcodeValue = "";
        if (code.length >= SCANNER_MIN_LENGTH) this.fetchOrder(code);
        return;
      }
      if (e.key.length === 1) this.barcodeValue += e.key;
    },

    async fetchOrder(code) {
      const [orderNum, lineNum] = code.split("-").map((s) => s.trim());
      try {
        const orderSnap = await this.ordersRef.doc(orderNum).get();
        if (!orderSnap.exists) {
          return Swal.fire({
            title: "No Records Found",
            html: "<b>No Records</b> has been found. <br/> Please refer to Diamond Track and confirm this order exists",
          });
        }

        const order = { id: orderSnap.id, ...orderSnap.data() };
        if (!lineNum) return this.handleViewModal(order);

        this.selectedOrder = order;
        const items = toDocs(
          await this.ordersRef.doc(order.id).collection("items").where("LineNum", "==", Number(lineNum)).get(),
        );

        if (!items.length) return Swal.fire("", `Line ${lineNum} has no items`, "info");
        if (items.length === 1) this.openQC(items[0]);
        else this.handleMultipleItemsQA(items, false);
      } catch (err) {
        console.error("Barcode lookup failed:", err);
        this.notify(`Lookup failed: ${err.message}`);
      }
    },

    /* ===================== order actions ===================== */

    deleteOrder(order) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await this.ordersRef.doc(order.id).delete();
          if (this.isSearchActive) {
            this.searchedData = this.searchedData.filter((o) => o.id !== order.id);
          }
          Swal.fire("Deleted!", "The data has been deleted.", "success");
        } catch (err) {
          Swal.fire("Error", err.message, "error");
        }
      });
    },

    duplicateAndReAssign(order) {
      this.selectedOrderForDuplication = order;
      this.showDuplicateModal = true;
    },

    /* ===================== order items modal ===================== */

    async bindOrderItems(order) {
      this.selectedOrder = order;
      this.selectedOrderLineStatus = order.LineItems || [];
      this.individualSearch = null;
      try {
        await this.$bind("allLineItemsUnfiltered", this.ordersRef.doc(order.id).collection("items"));
      } catch (err) {
        console.error("Failed to load items:", err);
        this.notify(`Failed to load items: ${err.message}`);
      }
    },

    async handleViewModal(order) {
      this.showDialog = true;
      await this.bindOrderItems(order);
    },

    async handleAuthViewModal(order) {
      this.showAuthViewModal = true;
      await this.bindOrderItems(order);
    },

    unbindOrderItems() {
      try {
        this.$unbind("allLineItemsUnfiltered");
      } catch (e) {
        /* not bound */
      }
      this.allLineItemsUnfiltered = [];
      this.allLineItems = [];
      this.individualSearch = null;
    },

    closeTableModal() {
      this.unbindOrderItems();
      this.selectedOrderLineStatus = [];
      this.selectedOrder = {};
      this.allItemsForSelectedLine = [];
      this.allFailedItems = [];
      this.selectedLineItem = {};
      this.selectedItem = {};
      this.showDialog = false;
    },

    closeAuthViewModal() {
      this.unbindOrderItems();
      this.selectedOrder = {};
      this.selectedOrderLineStatus = [];
      this.showAuthViewModal = false;
    },

    handleSearchForIndividualItem({ searchValue, searchKey }) {
      this.individualSearch = searchValue ? { searchValue: String(searchValue), searchKey } : null;
      this.applyIndividualSearch();
    },

    applyIndividualSearch() {
      const search = this.individualSearch;
      if (!search) {
        this.allLineItems = this.allLineItemsUnfiltered;
        return;
      }
      const { searchValue, searchKey } = search;
      this.allLineItems = this.allLineItemsUnfiltered.filter((i) =>
        searchKey === "LineNum"
          ? String(i[searchKey]) === searchValue
          : String(i[searchKey] ?? "").includes(searchValue),
      );
    },

    selectedLineItemAction(line) {
      this.selectedLineItem = line;
      this.allItemsForSelectedLine = this.allLineItems.filter((i) => i.LineNum == line.LineNum);
      this.showModalContentForLineItem = true;
      this.showModalContentForItem = false;
      this.showDialog = false;
    },

    selectedItemAction(item) {
      this.selectedItem = item;
      this.showModalContentForLineItem = false;
      this.showModalContentForItem = true;
      this.showDialog = false;
    },

    async saveDescription(line) {
      try {
        await this.ordersRef
          .doc(this.selectedOrder.id || this.selectedOrder.OrderNum)
          .collection("line_items")
          .doc(String(line.LineNum))
          .update({ Description: line.Description });
        this.showChangesBtn = false;
        this.notify("Changes has been saved successfully");
      } catch (err) {
        this.notify(`Error has occurred ${err.message}. Try again later or contact support`);
      }
    },

    /* ===================== images ===================== */

    handleImageModal(url) {
      this.imageView = url;
      this.showImageModal = true;
    },

    closeImageViewer() {
      this.showImageModal = false;
      this.imageView = null;
    },

    openImages(item, type = "failed") {
      this.selectedFailedItem = item;
      this.selectedItemType = type;
      this.showItemImagesModal = true;
    },

    openIndividualPhotoUpload() {
      this.selectedFailedItem = this.selectedItem;
      this.selectedItemType = "individual";
      this.showItemImagesModal = true;
    },

    closeImageModal() {
      this.showItemImagesModal = false;
      this.selectedFailedItem = {};
      this.selectedItemType = null;
    },

    itemDocRef(item) {
      const orderId = item.OrderNum || this.selectedOrder.id;
      return this.ordersRef.doc(String(orderId)).collection("items").doc(String(itemId(item)));
    },

    async removeImage(data, index) {
      if (this.selectedItemType === "line") {
        return this.notify("Open the individual item to remove its images");
      }
      const item = this.selectedFailedItem;
      const images = [...(item.Images || [])];
      images.splice(index, 1);
      try {
        await this.itemDocRef(item).update({ Images: images });
        this.$set(item, "Images", images);
      } catch (err) {
        this.notify(`Error removing image: ${err.message}`);
      }
    },

    uploadedImages(data) {
      if (this.selectedItemType === "failed") return this.uploadImageForFailedItem(data);
      if (this.selectedItemType === "individual") return this.uploadImageForIndividualItem(data);
      this.notify("Open the individual item to upload images");
    },

    async uploadImageForFailedItem(data) {
      const item = this.selectedFailedItem;
      try {
        await this.itemDocRef(item).update({
          Images: firebase.firestore.FieldValue.arrayUnion(data),
        });
        const images = item.Images || [];
        if (!images.some((img) => img.url === data.url)) this.$set(item, "Images", [...images, data]);
        this.notify("Image Uploaded Successfully");
        if (this.showOrderReportModal) this.showFailedItemReport(this.selectedOrder);
      } catch (err) {
        this.notify(`Error has occurred ${err.message}. Try again later or contact support`);
      }
    },

    async uploadImageForIndividualItem(data) {
      try {
        await this.itemDocRef(this.selectedItem).update({ ImageURL: data.url });
        this.$set(this.selectedItem, "ImageURL", data.url);
        this.notify("Image Uploaded Successfully");
        this.closeImageModal();
      } catch (err) {
        this.notify(`Error has occurred ${err.message}. Try again later or contact support`);
      }
    },

    /* ===================== single item Q/A ===================== */

    qcDocRef(item) {
      const orderId = this.selectedOrder.id || item.OrderNum;
      return this.ordersRef.doc(String(orderId)).collection("quality_control").doc(String(itemId(item)));
    },

    async openQC(item) {
      this.showModalContentForItem = false;
      if (item) this.selectedItem = item;
      try {
        const snap = await this.qcDocRef(this.selectedItem).get();
        // Always reset, so the previous item's answers never carry over.
        this.qcResponses = { rejections: [], notes: "", ...(snap.exists ? snap.data() : {}) };
        this.showQCModal = true;
      } catch (err) {
        this.notify(`Could not load Q/A: ${err.message}`);
      }
    },

    clearQCResponses() {
      this.showQCModal = false;
      this.leftQCPanelInit = false;
      this.qcResponses = { rejections: [], notes: "" };
    },

    handleRejectionSelection(data) {
      this.qcResponses.rejections = data.selectedRejections || [];
      this.qcResponses.notes = data.description || "";
      this.selectedItem.Status = "Failed"
      this.saveQC(this.selectedItem, this.qcResponses);
      if (this.activeUser.isSuperAdmin) this.showQAAssociateModal = true;
      else this.saveAuthChanges();
    },

    validateQCResponses(val) {
      return Boolean((val.rejections || []).length || this.leftQCPanelInit || val.notes);
    },

    async handleQCChanges() {
      if (!this.validateQCResponses(this.qcResponses)) {
        return Swal.fire("", "No changes has been detected, cannot continue without proper Q/A", "error");
      }
      const item = this.selectedItem;
      try {
        await this.qcDocRef(item).set(
          { ...this.qcResponses, createdBy: this.activeUser.uid },
          { merge: true },
        );
      } catch (err) {
        return this.notify(`Could not save Q/A: ${err.message}`);
      }

      this.certificateAPI({
        status: item.Status,
        certNum: item.CertNum,
        stockNum: item.ItemNum,
        trackingNum: item.TrackingNum || "",
      }).catch((err) => console.error("Certificate update failed:", err));

      this.clearQCResponses();
    },

    /* ===================== multiple item Q/A ===================== */

    handleMultipleItemsQA(items, throughAuth = true) {
      this.allFailedItems = items.map((i) => {
        i.qcResponses = { rejections: [], notes: "" };
        return i;
      });
      if (throughAuth) {
        this.showAuthModal = false;
        this.searchAuthItems = "";
      }
      this.isBeingHandledThroughAuthWindow = throughAuth;
      this.popupTab = 0;
      this.showQCModalForMult = true;
    },

    saveQC(item, responses) {
      item.qcResponses = responses;
      return this.ordersRef
        .doc(String(item.OrderNum))
        .collection("quality_control")
        .doc(String(itemId(item)))
        .set({ ...responses, createdBy: this.activeUser.uid }, { merge: true });
    },

    async handleQAPerItem(data, item, isLast) {
      try {
        await this.saveQC(item, {
          rejections: data.selectedRejections || [],
          notes: data.description || "",
        });
      } catch (err) {
        return this.notify(`Could not save Q/A: ${err.message}`);
      }
      if (isLast) this.finishQCProtocol();
      else this.changeItemTab(this.popupTab + 1);
    },

    // Applies the current tab's answers (and images) to this and all remaining failed items.
    async handleQAForAllSelectedItems(data) {
      const current = this.allFailedItems[this.popupTab];
      if (!current) return;

      const targets = this.allFailedItems.slice(this.popupTab).filter((i) => i.Status === "Failed");
      const responses = { rejections: data.selectedRejections || [], notes: data.description || "" };
      const images = current.Images || [];

      try {
        await Promise.all(
          targets.map((item) => {
            const tasks = [this.saveQC(item, { ...responses })];
            if (images.length && item !== current) {
              tasks.push(this.itemDocRef(item).update({ Images: images }));
            }
            return Promise.all(tasks);
          }),
        );
      } catch (err) {
        return this.notify(`Could not save Q/A for all items: ${err.message}`);
      }
      this.finishQCProtocol();
    },

    changeItemTab(tab) {
      this.popupTab = null;
      setTimeout(() => {
        this.popupTab = tab;
      }, 500);
    },

    finishQCProtocol() {
      this.popupTab = null;
      this.showQCModalForMult = false;
      if (!this.isBeingHandledThroughAuthWindow) return;
      if (this.activeUser.isSuperAdmin) this.showQAAssociateModal = true;
      else this.saveAuthChanges();
    },

    /* ===================== authentication ===================== */

    async handleAuthSearchOnce() {
      if (this.isSearchingAuth) return;
      this.isSearchingAuth = true;
      try {
        await this.handleAuthSearch();
      } finally {
        this.isSearchingAuth = false;
      }
    },

    async handleAuthSearch() {
      const raw = (this.searchAuthItems || "").trim();
      if (!raw) return Swal.fire("", "Enter an order or order-line number", "info");

      this.allItemsForAuthentication = [];
      this.selectedAuthItems = [];
      this.statusForAllItems = null;

      const [orderNum, lineNum] = raw.split("-").map((s) => s.trim());
      this.orderBeingAuthenticated = orderNum;

      let query = this.ordersRef.doc(orderNum).collection("items");
      if (lineNum) query = query.where("LineNum", "==", Number(lineNum));

      try {
        const snap = await query.orderBy("LineNum", "asc").get();
        if (snap.empty) return Swal.fire("Empty Search Records");
        // Remember the stored status so a cancelled change can be undone.
        this.allItemsForAuthentication = toDocs(snap).map((i) => ({ ...i, _originalStatus: i.Status }));
      } catch (err) {
        console.error("Auth search failed:", err);
        Swal.fire("Error", err.message, "error");
      }
    },

    upsertAuthItem(item) {
      if (!this.selectedAuthItems.some((i) => i.id === item.id)) this.selectedAuthItems.push(item);
    },

    handleSelectedAuthItem(item, action) {
      if (action === "remove") {
        this.selectedAuthItems = this.selectedAuthItems.filter((i) => i.id !== item.id);
        return;
      }
      if (!item.QCCompleted) {
        this.upsertAuthItem(item);
        return;
      }
      Swal.fire({
        title: "Are you sure?",
        text: "You are changing the status of an item that has been completed. You sure you want to make this change?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes!",
      }).then((result) => {
        if (result.value) {
          item.QCCompleted = false;
          item.QACompletedAt = null;
          item.QA_Associate = null;
          this.upsertAuthItem(item);
        } else {
          item.Status = item._originalStatus; // undo the radio change
        }
      });
    },

    handleStatuses(e, status) {
      const checked = e.target.checked;
      this.statusForAllItems = checked ? status : null;
      this.allItemsForAuthentication.forEach((item) => {
        if (item.QCCompleted) return;
        if (checked) {
          item.Status = status;
          this.handleSelectedAuthItem(item, "add");
        } else {
          item.Status = item._originalStatus;
          this.handleSelectedAuthItem(item, "remove");
        }
      });
    },

    handleAuthChanges() {
      const selected = this.selectedAuthItems;
      if (!selected.length) return;

      const failedNeedingQA = selected.filter((i) => i.Status === "Failed" && !i.QCCompleted);
      if (failedNeedingQA.length) {
        return Swal.fire(
          "QC Processing",
          "Failed Items Must Go To QC Processing First. Click Ok To QC Failed Items",
          "warning",
        ).then(() => this.handleMultipleItemsQA(failedNeedingQA, true));
      }

      if (this.activeUser.isSuperAdmin) this.showQAAssociateModal = true;
      else this.saveAuthChanges();
    },

    async saveAuthChanges() {
      if (!this.selectedQaAssociate) {
        return Swal.fire("Select QA Associate", "Please select QA Associate before moving forward", "error");
      }
      const items = this.selectedAuthItems.length ? this.selectedAuthItems : [this.selectedItem];
      const orderId = this.orderBeingAuthenticated || (items[0] && items[0].OrderNum);
      if (!orderId || !items.length) {
        return Swal.fire("Nothing to authenticate", "No items were selected.", "info");
      }

      const loader = this.$loading.show({ container: null, canCancel: false });
      try {
        const now = Date.now();
        for (let i = 0; i < items.length; i += BATCH_LIMIT) {
          const batch = db.batch();
          items.slice(i, i + BATCH_LIMIT).forEach((item) => {
            batch.update(this.ordersRef.doc(String(orderId)).collection("items").doc(item.id), {
              Status: item.Status,
              UserID: this.activeUser.uid,
              QA_Associate: this.selectedQaAssociate,
              QCCompleted: true,
              QACompletedAt: now,
            });
          });
          await batch.commit();
        }

        const payload = items.map(({ ...rest }) => rest);
        this.certificateAPI(payload).catch((err) => console.error(`Order ${orderId} certificate sync failed:`, err));

        Swal.fire("Authenticated", `Order #${orderId} has been authenticated`, "success");
        this.endAuthSequence();
      } catch (err) {
        console.error(err);
        Swal.fire("Error", err.message, "error");
      } finally {
        loader.hide();
      }
    },

    resetAuthState() {
      this.allItemsForAuthentication = [];
      this.selectedAuthItems = [];
      this.statusForAllItems = null;
      this.searchAuthItems = "";
      this.orderBeingAuthenticated = null;
      this.showAuthModal = false;
      this.showQAAssociateModal = false;
      this.isBeingHandledThroughAuthWindow = false;
    },

    closeAuthModal() {
      this.resetAuthState();
    },

    endAuthSequence() {
      this.resetAuthState();
      if (this.activeUser.isSuperAdmin) this.selectedQaAssociate = null;
    },

    /* ===================== order report ===================== */

    async showFailedItemReport(order) {
      const loader = this.$loading.show({ container: null, canCancel: false });
      try {
        this.selectedOrder = order;
        const orderDoc = this.ordersRef.doc(String(order.id || order.OrderNum));
        const [itemsSnap, qcSnap, linesSnap] = await Promise.all([
          orderDoc.collection("items").get(),
          orderDoc.collection("quality_control").get(),
          orderDoc.collection("line_items").get(),
        ]);

        // Build fresh objects, then assign once, so Vue tracks every key.
        const items = {};
        itemsSnap.docs.forEach((d) => {
          const data = d.data();
          items[d.id] = {
            ...data,
            id: d.id,
            Images: data.Images || [],
            Responses: { notes: "", rejections: [] },
          };
        });

        const lines = {};
        linesSnap.docs.forEach((d) => {
          lines[d.id] = {
            ...d.data(),
            failedQty: 0,
            skippedQty: 0,
            repairedQty: 0,
            Notes: [],
            Images: [],
          };
        });

        Object.values(items).forEach((item) => {
          const line = lines[String(item.LineNum)];
          if (!line) return;
          if (item.Status === "Failed") line.failedQty++;
          if (item.Status === "Skipped") line.skippedQty++;
          if (item.Status === "Passed-W/Repair") line.repairedQty++;
          item.Images.forEach((img) => {
            if (!line.Images.some((i) => i.name === img.name)) line.Images.push(img);
          });
        });

        qcSnap.docs.forEach((d) => {
          const item = items[d.id];
          if (!item) return;
          item.Responses = { notes: "", rejections: [], ...d.data() };
          const line = lines[String(item.LineNum)];
          if (line && item.Responses.notes && item.Responses.notes.trim()) {
            line.Notes.push(item.Responses.notes.trim());
          }
        });

        this.allOrderItemsInfo = items;
        this.allOrderLineInfo = lines;
        this.showOrderReportModal = true;
      } catch (err) {
        console.error("Report failed:", err);
        Swal.fire("Error", `Could not load the report: ${err.message}`, "error");
      } finally {
        loader.hide();
      }
    },

    closeReportModal() {
      this.selectedOrder = {};
      this.allOrderItemsInfo = {};
      this.allOrderLineInfo = {};
      this.reportSearchValue = "";
      this.showOrderReportModal = false;
      this.tableTab = "Line Item Status";
    },

    /* ===================== notes ===================== */

    handleNoteModal(item) {
      this.noteItem = item;
      this.noteDraft = (item.Responses && item.Responses.notes) || "";
      this.showNoteModal = true;
    },

    closeNoteModal() {
      this.showNoteModal = false;
      this.noteItem = null;
      this.noteDraft = "";
    },

    async handleSaveNote() {
      const item = this.noteItem;
      if (!item) return;
      try {
        // merge: only notes change; existing rejections are kept.
        await this.qcDocRef(item).set({ notes: this.noteDraft }, { merge: true });
        this.notify("Note has been saved");
        this.closeNoteModal();
        await this.showFailedItemReport(this.selectedOrder);
      } catch (err) {
        this.notify(`Could not save note: ${err.message}`);
      }
    },

    /* ===================== export ===================== */

    async exportCSV(action) {
      const formatDate = (v) => moment(v).format("MM/DD/YYYY");
      const loader = this.$loading.show({ container: null, canCancel: false });

      try {
        const payload =
          action === "all" ? (this.isSearchActive ? this.searchedData : this.tableData) : this.selectedExportData;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("QA Report");
        sheet.columns = [
          { header: "Date", key: "date", width: 12 },
          { header: "Order", key: "order", width: 16 },
          { header: "Vendor", key: "venue", width: 18 },
          { header: "Purchase Orders", key: "purchaseOrders", width: 20 },
          { header: "SKU", key: "sku", width: 18 },
          { header: "Certificate", key: "certificate", width: 18 },
          { header: "Status", key: "status", width: 14 },
          { header: "Rejections", key: "rejections", width: 40 },
          { header: "Images", key: "images", width: 20 },
        ];

        const processOrder = async (order) => {
          const orderDoc = this.ordersRef.doc(order.id);
          let itemsQuery = orderDoc.collection("items");
          if (this.filterBy.showFailedItems) itemsQuery = itemsQuery.where("Status", "==", "Failed");
          else if (this.filterBy.showPassedWithRepair) itemsQuery = itemsQuery.where("Status", "==", "Passed-W/Repair");
          else if (this.filterBy.showCertificateOrders) itemsQuery = itemsQuery.where("CertNum", "!=", "");

          const [itemsSnap, qaSnap] = await Promise.all([
            itemsQuery.get(),
            orderDoc.collection("quality_control").get(),
          ]);

          const qaNotes = {};
          qaSnap.forEach((d) => {
            const qa = d.data();
            qaNotes[d.id] = (qa.rejections || []).length
              ? qa.rejections.join(" ")
              : (qa.notes || "").replace(/[\r\n]/gm, " ");
          });

          return itemsSnap.docs
            .filter((d) => !d.id.includes("R"))
            .map((d) => {
              const item = d.data();
              const certOrItem = item.CertNum || item.ItemNum || "";
              return {
                date: formatDate(order.Timestamp),
                order: `${item.OrderNum}-${item.LineNum}`,
                venue: item.Customer || "",
                purchaseOrders: item.PO || (order.PurchaseOrders || [])[0] || "",
                sku: item.SKU || item.ClientSKU || "",
                certificate: certOrItem,
                status: item.Status || "",
                rejections: qaNotes[d.id] || "",
                imageLabel: certOrItem,
                imageUrl: (item.Images || [])[0]?.url || "",
                lineNum: item.LineNum,
              };
            })
            .sort((a, b) => a.lineNum - b.lineNum);
        };

        const rows = [];
        for (let i = 0; i < payload.length; i += 50) {
          const chunk = await Promise.all(payload.slice(i, i + 50).map(processOrder));
          rows.push(...chunk.flat());
        }

        rows.forEach((r) => {
          const { imageLabel, imageUrl, lineNum, ...cells } = r;
          const row = sheet.addRow(cells);
          const cell = row.getCell("images");
          const label = imageLabel || (imageUrl ? "View Image" : "");
          if (imageUrl) {
            cell.value = { text: label, hyperlink: imageUrl };
            cell.font = { color: { argb: "FF0563C1" }, underline: true };
          } else {
            cell.value = label;
          }
        });

        sheet.views = [{ state: "frozen", ySplit: 1 }];
        sheet.getRow(1).font = { bold: true };

        const buffer = await workbook.xlsx.writeBuffer({ useSharedStrings: true });
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `QA Report ${moment().format("LL")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Excel export failed:", err);
        Swal.fire("Export failed", err.message, "error");
      } finally {
        loader.hide();
        this.showExportOptions = false;
        this.showSelectableTable = false;
        this.selectedExportData = [];
      }
    },
  },
};
</script>

<style scoped>
/* ===== Layout ===== */
.md-layout {
  display: flex;
  justify-content: center;
  background: #f5f4f8;
}

.md-layout-item {
  max-width: 90vw;
  margin: 24px auto 40px;
  position: relative;
}

.md-button {
  z-index: 0 !important;
}

/* ===== Cards ===== */
.qc-card {
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06);
  margin-bottom: 16px;
  padding-top: 4px;
}

.auth-card {
  max-width: 100%;
}

/* ===== Search ===== */
.qc-search-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.qc-search-left {
  min-width: 220px;
}

.qc-search-label {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.qc-search-input-wrapper {
  flex: 1;
  min-width: 260px;
}

.qc-search-field {
  margin: 0;
}

.qc-search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-options ul {
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: #f3f4f6;
  list-style: none;
  margin: 0;
}

.search-options li {
  position: relative;
  margin: 0;
}

.search-options input[type="radio"] {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.search-options span {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  cursor: pointer;
  color: #4b5563;
}

.search-options input[type="radio"]:checked+span {
  background: #253c6e;
  color: #f9fafb;
}

.search-options input[type="radio"]:focus-visible+span {
  outline: 2px solid #253c6e;
  outline-offset: 2px;
}

/* ===== Buttons ===== */
.qc-btn {
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  text-transform: none;
}

.qc-btn-primary {
  background: #253c6e !important;
  color: #f9fafb !important;
}

.qc-btn-primary:hover {
  background: #020617 !important;
}

.qc-btn-secondary {
  background: #e5e7eb !important;
  color: #253c6e !important;
}

/* ===== Filters ===== */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 18px 6%;
}

.filter p {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 500;
  color: #4b5563;
}

.filter.customer {
  min-width: 200px;
}

.status-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
  font-size: 12px;
}

.filter-btn {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.bottom-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 0 10px;
}

/* ===== Table header / export / menu ===== */
.qc-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 15px;
}

.btn-row.stats_holder {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.qc-export-options {
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
}

.details-content {
  position: absolute;
  top: 11.2%;
  right: 0;
  z-index: 20;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.header {
  text-align: center;
  border-bottom: 0.5px dashed rgba(0, 0, 0, 0.49);
}

/* ===== Tables ===== */
.qc-table-wrapper {
  max-height: 400px;
  margin-top: 8px;
  overflow: auto;
}

.qc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #ffffff;
}

.qc-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #253c6e;
}

.qc-table th,
.qc-table td {
  text-align: center;
  vertical-align: middle;
}

.qc-table thead th {
  padding: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  color: #ffffff;
  white-space: nowrap;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
}

.qc-table thead th.no-sort {
  cursor: default;
}

.sort-icon {
  font-size: 9px;
  margin-left: 4px;
}

.qc-main-row td {
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.qc-main-row:nth-child(even) {
  background: #fafafa;
}

.qc-main-row:hover {
  background: #f3f4ff;
}

.qc-controls {
  white-space: nowrap;
}

.qc-strong,
.failed-items {
  font-weight: 900;
}

.icon-green {
  color: green !important;
}

.icon-gray {
  color: gray !important;
}

.icon-red {
  color: red !important;
}

.data-list {
  max-height: 200px;
  width: 250px;
  margin: 0 auto;
}

.data-list>ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 5px;
  max-height: 200px;
  max-width: 250px;
  overflow: auto;
  text-align: left;
  border: 0.5px solid black;
}

.data-list>ul>li {
  width: 85px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-content,
.no-data {
  color: #6b7280;
}

td.no-content {
  padding: 24px;
}

.sku-container {
  max-width: 100px;
  overflow-x: auto;
  margin: 0 auto;
}

.text-center {
  text-align: center;
}

/* ===== Pagination footer ===== */
.per-page {
  margin: 20px 0;
}

.per-page-select {
  border: none;
}

/* ===== Report: grouped rows ===== */
.qc-header-label {
  font-weight: 900;
}

.qc-cell-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.notes {
  width: 150px;
  max-height: 150px;
  overflow-y: auto;
  margin: 0;
  padding: 5px 5px 5px 18px;
  text-align: left;
}

.expanded_notes {
  width: 100%;
  max-height: 227px;
  overflow-y: auto;
  padding: 5px;
}

.qc-note-line {
  padding: 5px;
  text-align: left;
}

.result-list-item {
  text-decoration: underline;
  margin-bottom: 0.2rem;
}

.qc-line-header td {
  padding: 8px 10px;
  background: #eef1f8;
  color: #253c6e;
  font-weight: 600;
  text-align: left;
  border-top: 1px solid #dfe3ee;
}

.qc-line-count {
  margin-left: 8px;
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
}

.qc-line-passed,
.qc-line-failed,
.qc-line-repair,
.qc-line-skipped {
  font-weight: 600;
}

.qc-line-passed {
  color: green;
}

.qc-line-failed {
  color: #b91c1c;
}

.qc-line-repair {
  color: #395907;
}

.qc-line-skipped {
  color: orange;
}

.qc-shipped-date {
  font-weight: 900;
  color: green;
}



/* ===== Tabs ===== */
.toggle_tabs {
  display: flex;
  align-items: flex-end;
  width: 97%;
  margin: auto;
  overflow-x: auto;
}

.toggle_tabs .tab {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1% 3%;
  background: none;
  border: 0;
  border-bottom: 3px solid #eaeaea;
  font: inherit;
  font-weight: bold;
  text-transform: uppercase;
  color: rgba(27, 105, 111, 0.7);
  cursor: pointer;
}

.toggle_tabs .tab:focus-visible {
  outline: 2px solid #253c6e;
  outline-offset: 2px;
}

.toggle_tabs .tab.active {
  background: #0024d6 !important;
  border-bottom-color: #1f479a;
  color: #ffffff;
}

.toggle_tabs .tab p {
  margin: 0;
  padding: 1% 3%;
  font-size: 16px !important;
  font-weight: 700 !important;
}

.toggle_tabs .tab.active p {
  color: #ffffff !important;
}

/* ===== Modals ===== */
.field-fit {
  min-width: fit-content;
}

.field-120 {
  max-width: 120px;
}

.field-70 {
  max-width: 70px;
}

.item-image {
  margin-bottom: 16px !important;
}

.zoomable {
  cursor: zoom-in;
}

.image-container {
  display: flex;
  justify-content: center;
}

.no-image {
  width: 100%;
  height: 500px;
  padding-top: 28%;
  border: 0.5px dashed black;
  text-align: center;
  font-size: 20px;
}

.notes-container {
  display: flex;
  flex-direction: column;
}

.auth-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: white !important;
}

.barcode-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
}

/* ===== Transitions ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ===== Responsive ===== */
@media (max-width: 640px) {
  .qc-search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .qc-search-actions {
    justify-content: flex-end;
  }

  .bottom-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}

.qc-tracking-link {
  color: #253c6e !important;
  text-decoration: underline;
}
</style>